/**
 * 广告中心前端展示配置（数据库视图无法表达的 UI 元数据）
 *
 * - PageType：Tab 顺序与 Tab 标签文案
 * - SubType：每条广告位的标题、预览图、上传比例等
 */

// --- PageType（Tab 级）---

export const PAGE_TYPE_ORDER = [
  'bannerAd',
  'logoAd',
  'buttonAd',
  'landingAd',
  'videoAd',
  'starAd',
  'catListAd',
  'priorityAd',
];

export const PAGE_TYPE_I18N = {
  bannerAd: 'lbl_EMPBE_BannerAd',
  logoAd: 'lbl_EMPBE_LogoAd',
  buttonAd: 'lbl_EMPBE_ButtonAd',
  landingAd: 'lbl_EMPBE_LandingPageAd',
  videoAd: 'lbl_EMPBE_VideoAd',
  starAd: 'lbl_EMPBE_StarAd',
  catListAd: 'lbl_EMPBE_CatListAd',
  priorityAd: 'lbl_EMPBE_priorityAds',
};

export const PAGE_TYPE_T_LABEL = {
  bannerAd: 'EMPBE_AdInfoCenter_tab_Banner',
  logoAd: 'EMPBE_AdInfoCenter_tab_Logo',
  buttonAd: 'EMPBE_AdInfoCenter_tab_Button',
  landingAd: 'EMPBE_AdInfoCenter_tab_LandingPage',
  videoAd: 'EMPBE_AdInfoCenter_tab_Video',
  starAd: 'EMPBE_AdInfoCenter_tab_Star',
  catListAd: 'EMPBE_AdInfoCenter_tab_CatList',
  priorityAd: 'EMPBE_AdInfoCenter_tab_PriorityList',
};

/**
 * 列表行布局（左侧表单 + 右侧预览列）
 * - fullWidthCard：整行是否撑满（仅 banner 需要）
 * - previewFlex：右侧 adPreviewCardWrapper 的 flex，控制是否被拉伸
 */
export const PAGE_TYPE_LAYOUT = {
  bannerAd: { fullWidthCard: true, previewFlex: '1 1 auto' },
  logoAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
  buttonAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
  landingAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
  videoAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
  starAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
  catListAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
  priorityAd: { fullWidthCard: false, previewFlex: '0 1 auto' },
};

const DEFAULT_PAGE_TYPE_LAYOUT = { fullWidthCard: false, previewFlex: '0 1 auto' };

export function getPageTypeLayout(pageType) {
  return PAGE_TYPE_LAYOUT[pageType] || DEFAULT_PAGE_TYPE_LAYOUT;
}

/** 与 V_adPackagePageType.isUpload 一致：详情页含 media-uploader 的 PageType */
export const PAGE_TYPES_WITH_IMAGE_UPLOAD = ['bannerAd', 'buttonAd', 'landingAd'];

export function pageTypeHasImageUpload(pageType) {
  return PAGE_TYPES_WITH_IMAGE_UPLOAD.includes(pageType);
}

/** 前台 CPS/EMP 预览链接语言路径段（与 LangId 对应） */
export const DISPLAY_PREVIEW_LANG_SEGMENT_BY_LANG_ID = {
  936: 'simp',
  950: 'trad',
  1252: 'eng',
};

const DISPLAY_PREVIEW_LANG_SEGMENT_PATTERN = /\/(simp|trad|eng)(?=\/|\?|$)/i;
const DISPLAY_PREVIEW_LANG_PLACEHOLDER = '{lang}';

export function resolveDisplayPreviewLangSegment(langId) {
  const id = Number(langId ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('currentLangId') : null) ?? 936);
  return DISPLAY_PREVIEW_LANG_SEGMENT_BY_LANG_ID[id] || DISPLAY_PREVIEW_LANG_SEGMENT_BY_LANG_ID[936];
}

/** 将 URL 中的 {lang} 或 /simp|/trad|/eng 替换为当前语言段 */
export function applyDisplayPreviewLangPath(url, langId) {
  const raw = (url || '').trim();
  if (!raw) return '';
  const segment = resolveDisplayPreviewLangSegment(langId);
  if (raw.includes(DISPLAY_PREVIEW_LANG_PLACEHOLDER)) {
    return raw.split(DISPLAY_PREVIEW_LANG_PLACEHOLDER).join(segment);
  }
  if (DISPLAY_PREVIEW_LANG_SEGMENT_PATTERN.test(raw)) {
    return raw.replace(DISPLAY_PREVIEW_LANG_SEGMENT_PATTERN, `/${segment}`);
  }
  return raw;
}

export function resolveDisplayPreviewUrl(ad, langId) {
  if (!ad) return '';
  const base = (ad.displayPreviewUrl ?? ad.DisplayPreviewUrl ?? '').trim();
  return applyDisplayPreviewLangPath(base, langId);
}

/** 投放条件 → 前台搜索结果页 query 参数名（与 EMP AllExhibitors 一致） */
export const DISPLAY_PREVIEW_SEARCH_PARAM_BY_TYPE = {
  cat1: 'SProds',
  cat2: 'SProds',
  cat: 'SProds',
  customkeyword: 'Keyword',
  app: 'Apps',
  keyword: 'Tags',
};

/**
 * 从 AdBySearch 行解析预览链接用的动态值
 * - CustomKeyword → 输入框文本
 * - keyword（热搜）→ mst_KeywordReserved.MstTagId（SearchId 实为 KeywordId）
 * - 其余选择类 → searchId / updateValue
 */
export function resolveKeywordMstTagId(keywordId, keywordOptions = []) {
  const id = keywordId == null ? '' : String(keywordId).trim();
  if (!id || Number(id) < 0) return '';
  const opt = (keywordOptions || []).find(
    (x) => String(x.searchType || x.SearchType || '').toLowerCase() === 'keyword'
      && String(x.searchId ?? x.SearchId ?? '') === id,
  );
  const tagId = opt?.mstTagId ?? opt?.MstTagId;
  return tagId != null && String(tagId).trim() !== '' ? String(tagId).trim() : '';
}

export function resolveSearchItemPreviewValue(searchItem, options = {}) {
  if (!searchItem) return '';
  const type = String(searchItem.searchType || '').toLowerCase();
  if (type === 'customkeyword') {
    return String(searchItem.updateValue ?? searchItem.text ?? '').trim();
  }
  if (type === 'keyword') {
    const keywordId = searchItem.updateValue ?? searchItem.searchId;
    const fromItem = searchItem.mstTagId ?? searchItem.MstTagId;
    if (fromItem != null && String(fromItem).trim() !== '') {
      return String(fromItem).trim();
    }
    return resolveKeywordMstTagId(keywordId, options.keywordOptions);
  }
  const id = searchItem.updateValue ?? searchItem.searchId;
  if (id == null || id === '' || Number(id) < 0) return '';
  return String(id);
}

/** 将动态参数插入 URL 查询串最前（与前台链接格式一致） */
export function appendDisplayPreviewQueryParam(baseUrl, paramName, value) {
  const base = (baseUrl || '').trim();
  const name = (paramName || '').trim();
  const raw = value == null ? '' : String(value).trim();
  if (!base || !name || !raw) return base;
  const encoded = encodeURIComponent(raw);
  const qIndex = base.indexOf('?');
  if (qIndex < 0) return `${base}?${name}=${encoded}`;
  return `${base.slice(0, qIndex + 1)}${name}=${encoded}&${base.slice(qIndex + 1)}`;
}

/**
 * 按单条投放条件（产品类 / 行业 / 热搜 / 自定义关键字）生成带动态值的预览 URL
 * @param {string} baseUrl - 视图 V_adPackagePageType.displayPreviewUrl
 * @param {object} searchItem - AdBySearch 行（含 searchType、searchId、text 等）
 * @param {object} [options] - keywordOptions: GetSearchAdOptions 下拉项（含 mstTagId）
 */
export function buildDisplayPreviewUrlForSearchItem(baseUrl, searchItem, options = {}) {
  const base = applyDisplayPreviewLangPath((baseUrl || '').trim(), options.langId);
  if (!base || !searchItem) return base;
  const type = String(searchItem.searchType || '').toLowerCase();
  const paramName = DISPLAY_PREVIEW_SEARCH_PARAM_BY_TYPE[type];
  if (!paramName) return base;
  const value = resolveSearchItemPreviewValue(searchItem, options);
  if (!value) return '';
  return appendDisplayPreviewQueryParam(base, paramName, value);
}

// --- SubType（广告位级）---

/** renderMode: upload | companyLogo | productSelect | videoProduct */
export const AD_SUB_TYPE_META = {
  CPSLogo: {
    renderMode: 'companyLogo',
    titleKey: 'lbl_EMPBE_LogoAd_SearchResultPage',
    titlePKey: 'lbl_EMPBE_LogoAd_SearchResultPage_P',
    popupId: 'LogoSearchResultAd',
    previewImageFile: 'LogoSearchResultAd',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_LogoAd_SearchResultPage'],
    scrollToNums: 800,
    logoLocationIndex: 2,
    hasTriggeringCondition: true,
    section: 'cps',
  },
  EmpLogoExhibitorList: {
    renderMode: 'companyLogo',
    titleKey: 'lbl_EMPBE_LogoAd_ExhibitorTitle',
    titlePKey: 'lbl_EMPBE_LogoAd_ExhibitorTitle_P',
    popupId: 'LogoExhibitorList',
    previewImageFile: 'LogoExhibitorList',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_LogoAd_ExhibitorTitle'],
    scrollToNums: 800,
    logoLocationIndex: 1,
    section: 'cps',
  },
  EmpLogoHomepage: {
    renderMode: 'companyLogo',
    titleKey: 'lbl_EMPBE_LogoAd_HomePageTitle',
    titlePKey: 'lbl_EMPBE_LogoAd_HomePageTitle_P',
    popupId: 'LogoHomePageAd',
    previewImageFile: 'LogoHomePageAd',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_LogoAd_HomePageTitle'],
    scrollToNums: 3000,
    logoLocationIndex: 0,
    section: 'cps',
  },
  CPSStar: {
    renderMode: 'productSelect',
    titleKey: 'lbl_EMPBE_StarAd',
    titlePKey: 'lbl_EMPBE_StarAd_P',
    popupId: 'startAdRecords',
    previewImageFile: 'startAdRecordsNew',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_StarAd'],
    scrollToNums: 400,
    adType: 'starAd',
    section: 'cps',
    isHotApp: true,
    isPriority: false,
  },
  CPSCatList: {
    renderMode: 'productSelect',
    titleKey: 'lbl_EMPBE_CatListAd',
    titlePKey: 'lbl_EMPBE_CatListAd_P',
    popupId: 'catListAdRecords',
    previewImageFile: 'catListAd',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_HomePage', 'lbl_EMPBE_BannerMobile'],
    adType: 'catListAd',
    section: 'cps',
    isHotApp: false,
    isPriority: false,
  },
  CPSPriority: {
    renderMode: 'productSelect',
    titleKey: 'lbl_EMPBE_priorityAds',
    titlePKey: 'lbl_EMPBE_Ad_Top_Listing_p',
    popupId: 'priorityAdRecord',
    previewImageFile: 'priorityAdRecord',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_priorityAds'],
    adType: 'priorityAd',
    section: 'cps',
    isHotApp: true,
    isPriority: true,
  },
  EmpSkyBanner: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_Ad_HomePage_Central_Banner',
    titlePKey: 'lbl_EMPBE_Ad_HomePage_Central_Banner_P',
    popupId: 'HomepageCenteAd',
    previewImageFile: 'HomepageCenteAd',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_Ad_HomePage_Central_Banner'],
    scrollToNums: 300,
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    galleryStyle: 'height: 70px; width: 536px;',
    mobileGalleryStyle: 'height: 70px; width: 184px;',
    section: 'emp',
  },
  CPSInnerPage: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_AD_Banner',
    titlePKey: 'lbl_EMPBE_BannerDesktop_Title_P',
    popupId: 'exhibitionInfoFile',
    previewImageFile: 'exhibitionInfoFile',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_AD_Banner'],
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    galleryStyle: 'height: 70px; width: 536px;',
    mobileGalleryStyle: 'height: 70px; width: 184px;',
    section: 'cps',
    hasTriggeringCondition: true,
  },
  PreregConfirmEmail: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_AdPreregLetterBottomBanner',
    titlePKey: 'lbl_EMPBE_PreregLandingBottomBannerTitle_Title_P',
    popupId: 'preregLetterBottomBannerAd',
    // 与 banner/page.vue 一致：preregLetterBottomBannerAd_{langId}.png
    previewImageFile: 'preregLetterBottomBannerAd',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_AdPreregLetterBottomBanner'],
    scrollToNums: 200,
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    section: 'cps',
  },
  SearchResultBottom: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_Ad_SearchResult_Bottom_Banner',
    titlePKey: 'lbl_EMPBE_Ad_SearchResult_Bottom_Banner_P',
    popupId: 'empFileBottomInner',
    previewImageFile: 'empFileBottomInner',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_Ad_SearchResult_Bottom_Banner'],
    scrollToNums: 3000,
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    section: 'emp',
    hasTriggeringCondition: true,
  },
  PreregLandingBottom: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_AdPreregLandingBottomBanner',
    titlePKey: 'lbl_EMPBE_AdPreregLandingBottomBanner_Title_P',
    popupId: 'preregLandingBottomBanner',
    previewImageFile: 'AdPreregLandingBottomBanner',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_AdPreregLandingBottomBanner'],
    scrollToNums: 200,
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    section: 'cps',
  },
  EmpHomepageBottom: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_Ad_Homepage_Bottom_Banner',
    titlePKey: 'lbl_EMPBE_Ad_Homepage_Bottom_Banner_P',
    popupId: 'empFileBottom',
    previewImageFile: 'empFileBottom',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_Ad_Homepage_Bottom_Banner'],
    scrollToNums: 3000,
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    section: 'emp',
  },
  ExhibitorList: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_advertisingInfo_ExhibitorListTitle',
    titlePKey: 'lbl_EMPBE_advertisingInfo_ExhibitorListTitle_P',
    popupId: 'AdCPSOLExhibitorList',
    previewImageFile: 'AdCPSOLExhibitorList',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_advertisingInfo_ExhibitorListTitle'],
    aspectRatio: '7.7922:1',
    mobileAspectRatio: '2.6667:1',
    section: 'cps',
  },
  EmpButton: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_ButtonAd_Title',
    titlePKey: 'lbl_EMPBE_ButtonAd_Title_P',
    popupId: 'buttonExhibitionAd',
    previewImageFile: 'buttonInnerPageAd',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_ButtonAd_Title'],
    aspectRatio: '1.64948:1',
    galleryStyle: 'height: 90px; width: 150px;',
    uploadReqKey: 'lbl_EMPBE_BannerDesktopReqnew',
    section: 'cps',
  },
  EmpLanding: {
    renderMode: 'upload',
    titleKey: 'lbl_EMPBE_LandingPageAd',
    titlePKey: 'lbl_EMPBE_LandingPageAd_P',
    popupId: 'langdingHomePageAd',
    previewImageFile: 'langdingHomePageAd',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_landingAd_Title'],
    aspectRatio: '0.89887:1',
    galleryStyle: 'height: 178px; width: 160px;',
    section: 'emp',
  },
  CPSVideo: {
    renderMode: 'videoProduct',
    titleKey: 'lbl_EMPBE_VideoAd_CPSHomPageTitle',
    titlePKey: 'lbl_EMPBE_VideoAd_CPSHomPageTitle_P',
    popupId: 'CPSVideoAdRecord',
    previewImageFile: 'CPSVideoAdRecord',
    dialogTitleParts: ['lbl_EMPBE_ChinaPlasSite', 'lbl_EMPBE_VideoAd_CPSHomPageTitle'],
    scrollToNums: 2000,
    adType: 'videoAd',
    section: 'cps',
  },
  EmpVideo: {
    renderMode: 'videoProduct',
    titleKey: 'lbl_EMPBE_VideoAd_EMPHomPageTitle',
    titlePKey: 'lbl_EMPBE_VideoAd_EMPHomPageTitle_P',
    // 与 video/page.vue 一致：PopupId / 预览图均为 EmpHeaderAdRecord
    popupId: 'EmpHeaderAdRecord',
    previewImageFile: 'EmpHeaderAdRecord',
    dialogTitleParts: ['lbl_EMPBE_CPSeMarketplace', 'lbl_EMPBE_VideoAd_EMPHomPageTitle'],
    scrollToNums: 2000,
    adType: 'videoAd',
    section: 'emp',
  },
};

const DEFAULT_SUB_TYPE_META = {
  renderMode: 'upload',
  titleKey: 'lbl_EMPBE_BannerAd',
  titlePKey: 'lbl_EMPBE_BannerAd',
  popupId: 'empSkyBanner',
  previewImage: 'empSkyBanner',
  aspectRatio: '7.7922:1',
  galleryStyle: 'height: 70px; width: 536px;',
  section: 'emp',
};

export function getSubTypeMeta(subType) {
  const meta = AD_SUB_TYPE_META[subType];
  if (meta) return meta;
  return {
    ...DEFAULT_SUB_TYPE_META,
    popupId: `ad_${subType}`,
  };
}

export const DISPLAY_IN_I18N = {
  emp: 'lbl_EMPBE_CPSeMarketplace',
  cps: 'lbl_EMPBE_ChinaPlasSite',
};

/** 列表/筛选「展示平台」：与视图 V_adPackagePageType.isCPS 一致（0=emp，1=cps） */
export function getDisplayInI18nKeyFromIsCPS(isCPS) {
  return isCPS ? DISPLAY_IN_I18N.cps : DISPLAY_IN_I18N.emp;
}

export function matchesDisplaySectionFromIsCPS(isCPS, displaySection) {
  if (!displaySection) return true;
  const section = isCPS ? 'cps' : 'emp';
  return section === displaySection;
}

/** 旧版表单弹窗标题等仍按 subType.section；列表/筛选请用 isCPS */
export function getDisplayInI18nKey(subType) {
  const meta = getSubTypeMeta(subType);
  return DISPLAY_IN_I18N[meta.section] || DISPLAY_IN_I18N.emp;
}

/** 列表/详情「展示平台」列图标（CPS+ / EMP） */
export const AD_PLATFORM_LOGO = {
  cps: 'https://cdn.adsalecdn.com/EMP/Backend/svg/AdvertisingInfoCenter/backedCpsOrg.svg',
  emp: 'https://cdn.adsalecdn.com/EMP/Backend/svg/AdvertisingInfoCenter/backedEmpOrg.svg',
};

export function getAdPlatformLogoUrl(displaySection) {
  return displaySection === 'cps' ? AD_PLATFORM_LOGO.cps : AD_PLATFORM_LOGO.emp;
}

/** Figma 列表行状态（供应商与管理员共用；AI 审核中仍仅管理员细分） */
export const AD_ROW_STATUS = {
  live: 'live',
  pendingGoLive: 'pendingGoLive',
  pending: 'pending',
  pendingManualAudit: 'pendingManualAudit',
  rejected: 'rejected',
  toBeSubmitted: 'toBeSubmitted',
  expired: 'expired',
};

export const AD_ROW_STATUS_I18N = {
  // 与产品提交状态一致：已审批（勿用 lbl_EMPBE_isActivate，那是展示态「已上架」）
  live: 'lbl_EMPBE_Product_Approved',
  pendingGoLive: 'lbl_EMPBE_Ad_Status_PendingGoLive',
  pending: 'lbl_EMPBE_Ad_Status_Pending',
  pendingManualAudit: 'lbl_EMPBE_Ad_Status_PendingManualAudit',
  rejected: 'lbl_EMPBE_Rejected',
  toBeSubmitted: 'lbl_EMPBE_NotSubmitYet',
  expired: 'lbl_EMPBE_Ad_Status_Expired',
};

/** 管理员专用：与供应商共用 pending / pendingManualAudit（不再拆 AI） */
export const AD_ADMIN_ROW_STATUS_I18N = {
  ...AD_ROW_STATUS_I18N,
};

export function resolveAdStatusKey(row, fromAdmin = false) {
  if (!row) return '';
  if (fromAdmin && row.adminDisplayStatus) return row.adminDisplayStatus;
  return row.displayStatus || '';
}

export function resolveAdStatusI18nKey(statusKey, fromAdmin = false) {
  if (!statusKey) return '';
  if (fromAdmin) {
    return AD_ADMIN_ROW_STATUS_I18N[statusKey] || AD_ROW_STATUS_I18N[statusKey] || statusKey;
  }
  return AD_ROW_STATUS_I18N[statusKey] || statusKey;
}

/** 详情页「保存」：除已过期外各状态均显示（过期由 AdDetailView.showBottomActionBar 一并隐藏底部栏） */
export function canShowAdSaveButton(displayStatus) {
  return displayStatus !== AD_ROW_STATUS.expired;
}

/**
 * 广告中心埋点 t_label
 * 格式：EMPBE_AdInfoCenter_页面位置_按钮操作_[adDetailId_]公司id_角色id
 * adDetailId 有值时才插入
 */
export function buildAdInfoCenterTLabel(location, action, companyId, roleId, adDetailId) {
  const cid = Number(companyId) || 0;
  const rid = Number(roleId) || 0;
  const detailId = Number(adDetailId) || 0;
  const detailSegment = detailId > 0 ? `${detailId}_` : '';
  return `EMPBE_AdInfoCenter_${location}_${action}_${detailSegment}${cid}_${rid}`;
}

export function resolveAdInfoCenterTrackingRoleId(store) {
  const user = store?.state?.authentication?.user;
  return Number(user?.accountUserId) || 0;
}
